/**
 * Onboarding-Assistent (P2 Admin-Bereich V2, docs/KONZEPT-ADMIN-BEREICH.md §
 * "Onboarding-Assistent" + "Informationsarchitektur & UI": `/mandanten/neu`, 4 Schritte —
 * Stammdaten -> Owner -> Start-Konfiguration -> Fertig, docs/LOOP-ADMIN-BEREICH.md P2 siebtes
 * Kriterium). Erreichbar ueber den Knopf "Neuer Mandant" in `tenants-liste.tsx`, verdrahtet unter
 * dem bereits vom Mini-Router geparsten Ziel `mandanten-neu` (`hooks/useAdminNavigation.ts`) in
 * `pages/admin/index.tsx` — kein neues `<Route>`-Element, gleiches Muster wie ueberall sonst im
 * Admin-Bereich.
 *
 * Alle drei aufgerufenen Routen samt Antwortform sind 1:1 aus `apps/api/src/routes/admin.ts`
 * uebernommen, nicht geraten:
 * - `POST /api/admin/tenants` (Migration 376, `createTenantSchema`): `{slug, name}` ->
 *   `201 {data: NewTenantRow}` (`id, slug, name, stripeAccountId, createdAt, status`); `409
 *   slug_taken` bei bereits vergebenem Slug, `422 validation_error` bei ungueltigem/reserviertem
 *   Slug (Denylist `RESERVED_SLUGS`, s.u.).
 * - `POST /api/admin/tenants/:id/invites` (Migration 382, `createTenantInviteSchema`):
 *   `{email, role}` -> `201 {data: AdminInviteRow}`; `409 invite_pending` bei bereits offener
 *   Einladung fuer dieselbe E-Mail in diesem Mandanten, `404 not_found` bei unbekannter Id.
 * - Schritt 3 (Start-Konfiguration) ruft BEWUSST KEINE Route auf — s. Kopfkommentar dort.
 *
 * **Kein Zurueck nach Schritt 1:** die Mandanten-Anlage in Schritt 1 ist bereits geschehen (der
 * Mandant existiert danach echt in der Datenbank) - ein "Zurueck"-Knopf, der erneut denselben Slug
 * einreichen liesse, wuerde nur in einem erwartbaren, aber verwirrenden 409 `slug_taken` enden.
 * Ab Schritt 2 gibt es stattdessen "Später fortsetzen" (verlaesst den Assistenten, der bereits
 * angelegte Mandant bleibt unveraendert bestehen und ist ueber die normale Liste weiter erreichbar
 * - Owner-Einladung/Start-Konfiguration lassen sich jederzeit spaeter ueber die bestehenden
 * Mitglieder-/Einstellungen-Wege nachholen).
 */
import { type FormEvent, useState } from "react";
import { apiMutate } from "../../hooks/useApi";
import { ErrorNote, Icon } from "../../ui/components";
import { AvvPdfKnopf } from "../../ui/AvvSupportZugriff";

/** Antwortform von `platform_admin_tenant_anlegen` (Migration 376) — identisch zu `NewTenantRow`
 *  in `routes/admin.ts`. */
interface NeuerMandant {
  id: string;
  slug: string;
  name: string;
  stripeAccountId: string | null;
  createdAt: string;
  status: string;
}

/** Antwortform von `platform_admin_einladung_anlegen` (Migration 382) — identisch zu
 *  `AdminInviteRow` in `routes/admin.ts` (bewusst ohne Token, s. dortiger Kommentar). */
export interface OwnerEinladung {
  id: string;
  tenantId: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  /** Ist die Einladungsmail wirklich hinausgegangen? Nur dann darf die Oberflaeche „verschickt“ sagen. */
  mailSent?: boolean;
  /** Kein Mailer/kein MAIL_FROM konfiguriert - die Einladung liegt an, aber ohne Mail. */
  mailUnconfigured?: boolean;
}

/** Wahrheitsgemaesse Erfolgsmeldung nach einer Admin-Einladung (hier und in der Mandanten-Detailseite). */
export function einladungsMeldung(e: OwnerEinladung): string {
  if (e.mailSent) return `Einladung an ${e.email} wurde verschickt.`;
  if (e.mailUnconfigured) {
    return `Einladung an ${e.email} ist angelegt, aber es ging keine Mail hinaus: der Mailversand ist nicht eingerichtet.`;
  }
  return `Einladung an ${e.email} ist angelegt, aber die Mail konnte nicht verschickt werden.`;
}

/**
 * Live-Vorpruefung fuer Schritt 1 (Format + Denylist): `apps/web` kann `apps/api` nicht
 * importieren (getrennte Deployables, keine Workspace-Abhaengigkeit zwischen den beiden Paketen -
 * `apps/web/package.json` fuehrt ausschliesslich `@maestro/*`-Bibliothekspakete, nie `apps/api`
 * selbst) - dieselbe Duplizierung wie bereits zwischen der TS-Konstante `RESERVED_SLUGS`
 * (`apps/api/src/lib/tenant.ts`) und ihrem SQL-Backstop in `platform_admin_tenant_anlegen`
 * (Migrationskopf `packages/db/sql/376_platform_admin_tenant_anlegen.sql`), hier eine DRITTE
 * Kopie - ausschliesslich fuer sofortiges Client-Feedback waehrend der Eingabe. Sie entscheidet
 * NICHTS: der autoritative Nachweis bleibt serverseitig (`createTenantSchema` + DB-Backstop):
 * ein Absenden dieser Werte ohne die Live-Pruefung wuerde vom Server ohnehin exakt so abgelehnt.
 * Bei einer Erweiterung der Denylist muss diese Liste manuell mitgepflegt werden.
 */
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const RESERVED_SLUGS = new Set(["maestro", "www", "api", "admin", "app", "staging", "configzone"]);

type Schritt = 1 | 2 | 3 | 4;
const SCHRITT_LABEL: Record<Schritt, string> = {
  1: "Stammdaten",
  2: "Owner",
  3: "Start-Konfiguration",
  4: "Fertig",
};

/** Kopfzeile mit den vier Schritten — reine Anzeige, nicht anklickbar (anders als `TabBar`): der
 *  Ablauf hier ist eine echte Reihenfolge (Schritt 2 braucht die in Schritt 1 angelegte
 *  Mandanten-Id), kein freies Hin-und-Herspringen wie bei einer normalen Tab-Leiste. */
function SchrittLeiste({ aktuell }: { aktuell: Schritt }) {
  return (
    <ol className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-2">
      {([1, 2, 3, 4] as Schritt[]).map((s, i) => {
        const erledigt = s < aktuell;
        const aktiv = s === aktuell;
        return (
          <li key={s} className="flex items-center gap-2">
            {i > 0 && <Icon name="chevron_right" className="text-[16px] text-secondary-fixed-dim/60" />}
            <span
              className={`flex items-center gap-1.5 rounded-full px-3 py-1 font-label-md text-label-md ${
                aktiv
                  ? "bg-primary/10 font-semibold text-primary dark:bg-primary/20 dark:text-primary-fixed-dim"
                  : erledigt
                    ? "text-tertiary dark:text-[#b8ae9e]"
                    : "text-secondary-fixed-dim"
              }`}
            >
              {erledigt ? <Icon name="check_circle" className="text-[15px]" /> : <span>{s}.</span>}
              {SCHRITT_LABEL[s]}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function MandantNeuSeite({
  onAbbrechen,
  onFertig,
}: {
  /** Verlaesst den Assistenten zurueck zur Mandantenliste — vor Schritt 1 ein echtes "Abbrechen"
   *  (noch nichts angelegt), ab Schritt 2 ein "Später fortsetzen" (s. Kopfkommentar). */
  onAbbrechen: () => void;
  /** Schliesst den Assistenten erfolgreich ab und zeigt die Detailseite des neu angelegten
   *  Mandanten (dort liegt bereits "Support-Zugriff starten"/Impersonation, s. Kopfkommentar). */
  onFertig: (tenantId: string) => void;
}) {
  const [schritt, setSchritt] = useState<Schritt>(1);
  const [tenant, setTenant] = useState<NeuerMandant | null>(null);

  // Schritt 1: Stammdaten.
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [stammdatenBusy, setStammdatenBusy] = useState(false);
  const [stammdatenError, setStammdatenError] = useState<string | null>(null);

  const slugTrim = slug.trim().toLowerCase();
  const slugFormatFehler = slugTrim !== "" && !SLUG_RE.test(slugTrim);
  const slugReserviert = slugTrim !== "" && RESERVED_SLUGS.has(slugTrim);
  const slugHinweis = slugFormatFehler
    ? "Ungültiger Slug (Kleinbuchstaben/Ziffern/Bindestrich, 1-63 Zeichen, nicht am Rand)."
    : slugReserviert
      ? "Dieser Slug ist reserviert und kann nicht vergeben werden."
      : null;
  const stammdatenGueltig = name.trim() !== "" && slugTrim !== "" && !slugFormatFehler && !slugReserviert;

  async function submitStammdaten(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!stammdatenGueltig || stammdatenBusy) return;
    setStammdatenBusy(true);
    setStammdatenError(null);
    try {
      const angelegt = await apiMutate<NeuerMandant>("/api/admin/tenants", "POST", { slug: slugTrim, name: name.trim() });
      setTenant(angelegt);
      setSchritt(2);
    } catch (err) {
      // Server liefert bei 409 slug_taken bereits einen fertigen deutschen Text (s.
      // Kopfkommentar) - direkt zeigen, kein stiller Fehlschlag.
      setStammdatenError(err instanceof Error ? err.message : String(err));
    } finally {
      setStammdatenBusy(false);
    }
  }

  // Schritt 2: Owner-Einladung. Rolle bewusst fest "owner" (kein Auswahlfeld) - dieser Schritt
  // heisst im Konzept ausdruecklich "Owner", nicht "Mitglied einladen" (das bleibt der bestehende,
  // freie Weg im Mitglieder-Tab der Detailseite).
  const [ownerEmail, setOwnerEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteResult, setInviteResult] = useState<OwnerEinladung | null>(null);
  const [inviteUebersprungen, setInviteUebersprungen] = useState(false);

  async function sendeEinladung(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!tenant || !ownerEmail.trim() || inviteBusy) return;
    setInviteBusy(true);
    setInviteError(null);
    try {
      const invite = await apiMutate<OwnerEinladung>(`/api/admin/tenants/${tenant.id}/invites`, "POST", {
        email: ownerEmail.trim(),
        role: "owner",
      });
      setInviteResult(invite);
      setInviteUebersprungen(false);
      setSchritt(3);
    } catch (err) {
      // 409 invite_pending liefert bereits einen fertigen deutschen Text (s. Kopfkommentar).
      setInviteError(err instanceof Error ? err.message : String(err));
    } finally {
      setInviteBusy(false);
    }
  }

  function ueberspringenEinladung() {
    setInviteError(null);
    setInviteResult(null);
    setInviteUebersprungen(true);
    setSchritt(3);
  }

  return (
    <div className="mt-6 max-w-2xl">
      <button type="button" className="btn-secondary mb-4" onClick={onAbbrechen}>
        <Icon name="arrow_back" className="text-[16px]" /> {schritt === 1 ? "Abbrechen" : "Später fortsetzen"}
      </button>

      <div className="card overflow-hidden p-6">
        <h1 className="mb-1 font-body-md text-body-md font-bold text-on-background dark:text-inverse-on-surface">
          Neuer Mandant
        </h1>
        <p className="mb-5 font-body-sm text-[12.5px] text-secondary-fixed-dim">
          Legt einen neuen Betrieb an, lädt optional dessen Inhaber per E-Mail ein und zeigt die
          nötigen Start-Einstellungen.
        </p>

        <SchrittLeiste aktuell={schritt} />

        {schritt === 1 && (
          <form className="space-y-4" onSubmit={(e) => void submitStammdaten(e)}>
            <label className="block">
              <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Name
              </span>
              <input
                type="text"
                required
                autoFocus
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="z. B. Storia GmbH"
              />
            </label>
            <label className="block">
              <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Slug (Subdomain)
              </span>
              <input
                type="text"
                required
                className="input font-mono"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="z. B. storia"
              />
              <p className="mt-1.5 font-body-sm text-[12px] text-secondary-fixed-dim">
                Erreichbar unter <span className="font-mono">{slugTrim || "…"}.schrittmacher.ai</span>
              </p>
              {slugHinweis && <p className="mt-1 font-body-sm text-[12px] text-error">{slugHinweis}</p>}
            </label>
            {stammdatenError && <ErrorNote message={stammdatenError} />}
            <div className="flex justify-end">
              <button type="submit" className="btn-primary" disabled={!stammdatenGueltig || stammdatenBusy}>
                {stammdatenBusy ? "Legt an …" : "Weiter"}
              </button>
            </div>
          </form>
        )}

        {schritt === 2 && tenant && (
          <div>
            <p className="mb-4 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
              Mandant „{tenant.name}" (<span className="font-mono">{tenant.slug}</span>) wurde angelegt. Lade
              jetzt optional den Inhaber per E-Mail ein — die Einladung läuft über das bestehende Einladungssystem
              und kann übersprungen werden.
            </p>
            <form className="space-y-4" onSubmit={(e) => void sendeEinladung(e)}>
              <label className="block">
                <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                  E-Mail des Inhabers
                </span>
                <input
                  type="email"
                  className="input"
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  placeholder="inhaber@betrieb.de"
                />
              </label>
              {inviteError && <ErrorNote message={inviteError} />}
              <div className="flex justify-end gap-2.5">
                <button type="button" className="btn-secondary" onClick={ueberspringenEinladung} disabled={inviteBusy}>
                  Überspringen
                </button>
                <button type="submit" className="btn-primary" disabled={inviteBusy || !ownerEmail.trim()}>
                  {inviteBusy ? "Sendet …" : "Einladung senden"}
                </button>
              </div>
            </form>
          </div>
        )}

        {schritt === 3 && tenant && (
          <div>
            {/* Start-Konfiguration bewusst READ-ONLY/informativ: die editierbare Route dafuer
                (`platform_admin_tenant_settings_lesen`/`_aktualisieren`, Migration 378) gehoert zu
                P4 und existiert noch nicht (geprueft: `grep -rn
                platform_admin_tenant_settings_lesen apps/api/src/routes/admin.ts
                packages/db/sql/` liefert nichts, Stand wie in `docs/LOOP-ADMIN-BEREICH.md` P1
                bereits fuer den Einstellungen-Tab dokumentiert). Diese Iteration erfindet dafuer
                KEINE Route - Auftrag/Konzept erlauben ausdruecklich einen rein informativen
                Platzhalter. Die genannten Werte (`agentMode: "off"`, `mailFreigabeModus:
                "manuell"`) sind KEINE Behauptung ueber den tatsaechlichen Datenbank-Zustand
                dieses frischen Mandanten (dafuer gibt es admin-seitig noch keine Leseroute) - sie
                beschreiben die Werkseinstellung neuer Mandanten laut Konzept
                (docs/KONZEPT-ADMIN-BEREICH.md § "Onboarding-Assistent": "sichere Defaults:
                `agentMode` `off`, `mailFreigabeModus` `manuell`"). */}
            <p className="mb-3 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
              Neue Mandanten starten mit sicheren Standardwerten:
            </p>
            <ul className="mb-4 space-y-2">
              <li className="flex items-start gap-2 font-body-sm text-[13px] text-on-background dark:text-inverse-on-surface">
                <Icon name="smart_toy" className="mt-0.5 text-[16px] text-secondary-fixed-dim" />
                Agenten-Modus: <strong className="font-semibold">aus</strong> — der Betrieb aktiviert die
                KI-Bedienung selbst, sobald er bereit ist.
              </li>
              <li className="flex items-start gap-2 font-body-sm text-[13px] text-on-background dark:text-inverse-on-surface">
                <Icon name="mail" className="mt-0.5 text-[16px] text-secondary-fixed-dim" />
                Mail-Freigabe: <strong className="font-semibold">manuell</strong> — jede ausgehende Mail
                braucht zunächst eine Freigabe im Betrieb.
              </li>
            </ul>
            <p className="mb-4 font-body-sm text-[12px] text-secondary-fixed-dim">
              Eine eigene Bearbeitungsfläche für diese Einstellungen im Admin-Bereich ist geplant, aber noch
              nicht gebaut — bis dahin ändert der Betrieb sie selbst in seinen eigenen Einstellungen.
            </p>
            <div className="flex justify-end">
              <button type="button" className="btn-primary" onClick={() => setSchritt(4)}>
                Weiter
              </button>
            </div>
          </div>
        )}

        {schritt === 4 && tenant && (
          <div>
            <ul className="mb-5 space-y-3">
              <li className="flex items-start gap-2.5">
                <Icon name="check_circle" className="mt-0.5 text-[18px] text-tertiary dark:text-[#b8ae9e]" />
                <span className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                  Mandant „{tenant.name}" (<span className="font-mono">{tenant.slug}</span>) wurde angelegt.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <Icon
                  name={inviteResult?.mailSent ? "check_circle" : "info"}
                  className={`mt-0.5 text-[18px] ${inviteResult?.mailSent ? "text-tertiary dark:text-[#b8ae9e]" : "text-secondary-fixed-dim"}`}
                />
                <span className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                  {inviteResult
                    ? einladungsMeldung(inviteResult)
                    : inviteUebersprungen
                      ? "Owner-Einladung übersprungen — kann jederzeit im Mitglieder-Tab nachgeholt werden."
                      : "Keine Owner-Einladung verschickt."}
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <Icon name="check_circle" className="mt-0.5 text-[18px] text-tertiary dark:text-[#b8ae9e]" />
                <span className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                  Start-Konfiguration verwendet die sicheren Standardwerte (Agent aus, Mail-Freigabe manuell).
                </span>
              </li>
            </ul>
            <div className="mb-5 space-y-3 rounded-xl border border-outline-variant/40 p-4 dark:border-outline/30">
              <p className="font-label-md text-label-md font-semibold uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Unterlagen für den Inhaber
              </p>
              <AvvPdfKnopf betrieb={tenant.name} label="AVV als PDF herunterladen" />
              <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                Derselbe Text steht für den Inhaber jederzeit unter Einstellungen → Datenschutz.
              </p>
            </div>
            <div className="flex justify-end">
              <button type="button" className="btn-primary" onClick={() => onFertig(tenant.id)}>
                Zur Mandanten-Detailseite <Icon name="arrow_forward" className="text-[16px]" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
